/**
 * Admin user roster — Phase 3.2.c.
 *
 * Pulled out of admin.js so the technicians + tickets-cluster views can
 * `import` the loaders/getters directly instead of receiving them via
 * `configure({helpers})`.
 *
 * Owns:
 *   - The cached lists: `technicianAdminUsers`, `supervisorAdminUsers`,
 *     `techSupervisorAdminUsers`.
 *   - The technician *roster* (the canonical record with display_name +
 *     admin_user_id linkage) and its `technicianProfileByAdminId` lookup.
 *   - Per-list `loaded`/`loading` flags so concurrent callers de-dupe.
 *
 * Cross-view callbacks (populateAssignmentOptions, populateSupervisorFilter,
 * etc.) are reached lazily via `window.adminViews.*` so this module
 * doesn't import sibling view modules — that would create circular
 * dependencies and tighten coupling.
 */

import { fetch as apiFetch, routes as API } from '../../js/api-client.js';
import { showFlash } from '../../js/notifications.js';
import { extractRoleNames, FIELD_TECH_SUPERVISOR_ROLE } from './ticket-utils.js';

// ----- State ----------------------------------------------------------

let technicianAdminUsers = [];
let technicianUsersLoaded = false;
let technicianUsersLoading = false;

let supervisorAdminUsers = [];
let supervisorUsersLoaded = false;
let supervisorUsersLoading = false;

let techSupervisorAdminUsers = [];
let techSupervisorUsersLoaded = false;
let techSupervisorUsersLoading = false;

let technicianRosterLoaded = false;
let technicianRosterLoading = false;

const technicianProfileByAdminId = new Map();

// ----- Getters --------------------------------------------------------

export function getTechnicianAdminUsers() { return technicianAdminUsers; }
export function getSupervisorAdminUsers() { return supervisorAdminUsers; }
export function getTechSupervisorAdminUsers() { return techSupervisorAdminUsers; }
export function getTechnicianRosterState() {
  return { loaded: technicianRosterLoaded, loading: technicianRosterLoading };
}
export function setTechnicianRosterState(state) {
  if (!state) return;
  technicianRosterLoaded = !!state.loaded;
  technicianRosterLoading = !!state.loading;
}
export function getTechnicianProfileByAdminId(adminId) {
  return technicianProfileByAdminId.get(adminId) || null;
}

// ----- Loaders --------------------------------------------------------

function updateTechnicianProfiles(items) {
  if (!Array.isArray(items)) return;
  items.forEach((tech) => {
    if (!tech || tech.admin_user_id == null) return;
    const adminId = Number(tech.admin_user_id);
    if (Number.isNaN(adminId)) return;
    technicianProfileByAdminId.set(adminId, tech);
  });
}

export async function ensureTechnicianRosterLoaded(options = {}) {
  const force = Boolean(options.force);
  if ((!force && technicianRosterLoaded) || technicianRosterLoading) return;
  technicianRosterLoading = true;
  try {
    const data = await apiFetch(API.adminTechnicians, { method: 'GET' });
    const items = Array.isArray(data?.items) ? data.items : (Array.isArray(data) ? data : []);
    updateTechnicianProfiles(items);
    // The technicians module owns `techniciansState`; seed it if empty.
    const t = window.adminViews && window.adminViews.technicians;
    if (t && typeof t.getState === 'function') {
      const state = t.getState();
      if (state && Array.isArray(state.items) && !state.items.length) state.items = items;
    }
    technicianRosterLoaded = true;
  } catch {
    // swallow roster errors; dedicated views will surface failures
  } finally {
    technicianRosterLoading = false;
  }
}

export async function ensureTechnicianUsersLoaded(options = {}) {
  const force = Boolean(options.force);
  if ((!force && technicianUsersLoaded) || technicianUsersLoading) return;
  technicianUsersLoading = true;
  try {
    const params = new URLSearchParams();
    params.set('limit', '200');
    params.set('skip', '0');
    const data = await apiFetch(`${API.users}?${params.toString()}`, { method: 'GET' });
    const items = Array.isArray(data) ? data : (data.items || []);
    technicianAdminUsers = items.map((user) => ({
      ...user,
      roles: Array.isArray(user?.roles) ? user.roles.slice() : [],
    }));
    await ensureTechnicianRosterLoaded();
    technicianUsersLoaded = true;
    // Cross-view callbacks — looked up lazily so this module doesn't
    // import its siblings.
    const tickets = window.adminViews && window.adminViews.tickets;
    if (tickets) {
      if (tickets.list && typeof tickets.list.populateAssigneeUserFilter === 'function') {
        tickets.list.populateAssigneeUserFilter();
      }
      if (tickets.create && typeof tickets.create.populateAssignmentOptions === 'function') {
        tickets.create.populateAssignmentOptions();
      }
      if (tickets.pendingMessages && tickets.pendingMessages.detail
          && typeof tickets.pendingMessages.detail.populateAssignmentOptions === 'function') {
        tickets.pendingMessages.detail.populateAssignmentOptions();
      }
    }
    const technicians = window.adminViews && window.adminViews.technicians;
    if (technicians && typeof technicians.populateAdminUserOptions === 'function') {
      technicians.populateAdminUserOptions();
    }
  } catch (err) {
    showFlash(err.message || 'Failed to load admin users', 'error');
  } finally {
    technicianUsersLoading = false;
  }
}

export async function ensureSupervisorUsersLoaded(options = {}) {
  const force = Boolean(options.force);
  if ((!force && supervisorUsersLoaded) || supervisorUsersLoading) return;
  supervisorUsersLoading = true;
  try {
    const data = await apiFetch(API.adminSupervisors, { method: 'GET' });
    const items = Array.isArray(data?.items) ? data.items : [];
    supervisorAdminUsers = items.map((user) => ({
      ...user,
      roles: Array.isArray(user?.roles) ? user.roles.slice() : [],
    }));
    techSupervisorAdminUsers = supervisorAdminUsers.filter((user) => {
      const roles = extractRoleNames(user);
      return roles.includes(FIELD_TECH_SUPERVISOR_ROLE);
    });
    supervisorUsersLoaded = true;
    const tickets = window.adminViews && window.adminViews.tickets;
    if (tickets) {
      if (tickets.create && typeof tickets.create.populateAssignmentOptions === 'function') {
        tickets.create.populateAssignmentOptions();
      }
      if (tickets.pendingMessages && tickets.pendingMessages.detail
          && typeof tickets.pendingMessages.detail.populateAssignmentOptions === 'function') {
        tickets.pendingMessages.detail.populateAssignmentOptions();
      }
      if (tickets.technicianBoard
          && typeof tickets.technicianBoard.populateSupervisorFilter === 'function') {
        tickets.technicianBoard.populateSupervisorFilter();
      }
    }
  } catch (err) {
    showFlash(err.message || 'Failed to load supervisors', 'error');
  } finally {
    supervisorUsersLoading = false;
  }
}

export async function ensureTechSupervisorUsersLoaded(options = {}) {
  const force = Boolean(options.force);
  if ((!force && techSupervisorUsersLoaded) || techSupervisorUsersLoading) return;
  techSupervisorUsersLoading = true;
  try {
    const params = new URLSearchParams();
    params.set('role', FIELD_TECH_SUPERVISOR_ROLE);
    const data = await apiFetch(`${API.adminSupervisors}?${params.toString()}`, { method: 'GET' });
    const items = Array.isArray(data?.items) ? data.items : [];
    techSupervisorAdminUsers = items.map((user) => ({
      ...user,
      roles: Array.isArray(user?.roles) ? user.roles.slice() : [],
    }));
    techSupervisorUsersLoaded = true;
    const tickets = window.adminViews && window.adminViews.tickets;
    if (tickets && tickets.technicianBoard
        && typeof tickets.technicianBoard.populateSupervisorFilter === 'function') {
      tickets.technicianBoard.populateSupervisorFilter();
    }
  } catch (err) {
    showFlash(err.message || 'Failed to load supervisors', 'error');
  } finally {
    techSupervisorUsersLoading = false;
  }
}

export { updateTechnicianProfiles };
